import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Ref } from "vue";
import { shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type {
  BuyPricingRequest,
  BuyPricingResponse,
  GlobalPurchaseSettings,
  PricingState,
} from "../bulk_buy/bulkBuyTypes";
import { PricingStatus } from "../bulk_buy/bulkBuyTypes";
import {
  getPricingLoadingMessages,
  runPricingLoadingSequence,
} from "../common/pricingLoadingUtils";

export function useBuyPricing(
  volume: Ref<number>,
  country: Ref<string | undefined>,
  globalSettings: Ref<GlobalPurchaseSettings>
) {
  const { t } = useI18n();
  const { globalErrorHandler } = useGlobalHandlers();
  const pricingState = shallowRef<PricingState>({ status: "idle" });
  let requestToken = 0;

  const clearPricing = () => {
    requestToken += 1;
    pricingState.value = { status: "idle" };
  };

  const fetchPricing = async () => {
    if (!volume.value || volume.value < 1) {
      pricingState.value = {
        status: "error",
        message: t("bulk_buy.volume_required"),
      };
      return;
    }

    const token = ++requestToken;

    try {
      const loadingMessages = getPricingLoadingMessages((key) => t(key));

      const request: BuyPricingRequest = {
        volume: volume.value,
        country: country.value,
        include_cancellation: false,
        global_settings: globalSettings.value,
      };

      const response = await runPricingLoadingSequence(
        (state) => {
          if (token !== requestToken) return;
          pricingState.value = state;
        },
        loadingMessages,
        goSolidApi.post<BuyPricingResponse>("/api/eac/buy/pricing", request)
      );

      if (token !== requestToken) return;

      const pricing = response.data;

      if (
        pricing.status === PricingStatus.PRICE_FOUND &&
        pricing.total_price !== undefined
      ) {
        pricingState.value = {
          status: "success",
          price: Number(pricing.total_price),
          price_date: pricing.price_date ?? undefined,
          pricing_request_id: pricing.request_id,
        };
      } else {
        pricingState.value = {
          status: "agent_reach_out",
          pricing_request_id: pricing.request_id ?? undefined,
        };
      }
    } catch (error) {
      if (token !== requestToken) return;
      globalErrorHandler(error);
      pricingState.value = {
        status: "error",
        message: t("bulk_buy.pricing_error"),
      };
    }
  };

  watch([volume, country, globalSettings], clearPricing, { deep: true });

  return {
    pricingState,
    fetchPricing,
    clearPricing,
  };
}
